import React, { Fragment, useState, useEffect } from 'react';
import {
  UserCheck,
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  Loader2,
  X,
  FolderOpen,
  Download,
  Paperclip,
  FileText,
  AlertCircle,
  CheckCircle,
  Building2,
  Layers,
  Send,
  Mail,
  Copy,
  Clock,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  CheckCircle2,
  XCircle,
  History,
  Link2,
} from 'lucide-react';
import { clientQualificationApi, clientRequirementAnalysisApi, clientDocumentsApi, sectorsApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError, validateDateFields, validateDateField, formatDateBR } from '../utils/dateValidation';
import { RequirementAnalysisFormFields, buildRequirementFormData } from './ClientRequirementAnalysisForm';

// Documentos para Qualificação de Clientes

const DOCUMENT_ITEMS = [
  { key: 'inscricao_estadual', label: 'Inscrição Estadual' },
  { key: 'inscricao_municipal', label: 'Inscrição Municipal' },
  { key: 'sintegra', label: 'SINTEGRA' },
  { key: 'alvara_funcionamento', label: 'Alvará de Funcionamento' },
  { key: 'avcb', label: 'Auto de Vistoria do Corpo de Bombeiros – AVCB' },
  { key: 'licenca_funcionamento_sanitaria', label: 'Licença de Funcionamento Sanitária' },
  { key: 'autorizacao_funcionamento_anvisa', label: 'Autorização de Funcionamento (AFE/AE – ANVISA)' },
  { key: 'certidao_responsabilidade_tecnica', label: 'Certidão de Responsabilidade Técnica' },
  { key: 'certificado_boas_praticas_fabricacao', label: 'Certificado de Boas Práticas de Fabricação' },
  { key: 'licenca_produtos_perigosos', label: 'Licença de Produtos Perigosos (Civil, Federal, Exército)' },
];

const PRODUCT_CLASSES = [
  { key: 'saneantes', label: 'Saneantes' },
  { key: 'correlatos', label: 'Correlatos' },
  { key: 'cosmeticos_higiene', label: 'Cosméticos/Produtos de Higiene' },
  { key: 'alimentos', label: 'Alimentos' },
  { key: 'nao_interesse_saude', label: 'Não Interesse à Saúde' },
  { key: 'nao_perigosos', label: 'Não Perigosos' },
  { key: 'nao_perigosos_e_perigosos', label: 'Não Perigosos e Perigosos' },
  { key: 'perigosos', label: 'Perigosos' },
  { key: 'quimicos', label: 'Químicos' },
  { key: 'outros', label: 'Outros' },
];

const STATUS_CONFIG = {
  draft: {
    label: 'Aguardando preenchimento',
    badge: 'badge-gray',
    color: 'bg-gray-500/20 text-gray-400',
    icon: Send,
  },
  pending_analysis: {
    label: 'Pendente de análise',
    badge: 'badge-yellow',
    color: 'bg-yellow-500/20 text-yellow-400',
    icon: Clock,
  },
  in_analysis: {
    label: 'Em análise',
    badge: 'badge-blue',
    color: 'bg-blue-500/20 text-blue-400',
    icon: Search,
  },
  approved: {
    label: 'Aprovado',
    badge: 'badge-green',
    color: 'bg-green-500/20 text-green-400',
    icon: CheckCircle2,
  },
  correction_needed: {
    label: 'Necessita correção',
    badge: 'badge-red',
    color: 'bg-red-500/20 text-red-400',
    icon: XCircle,
  },
};

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'draft', label: 'Aguardando preenchimento' },
  { value: 'pending_analysis', label: 'Pendente de análise' },
  { value: 'in_analysis', label: 'Em análise' },
  { value: 'correction_needed', label: 'Necessita correção' },
  { value: 'approved', label: 'Aprovado' },
];

const STATUS_STEP_LABELS = {
  sent: 'Formulário enviado',
  submitted: 'Preenchido pelo cliente',
  resubmitted: 'Reenviado pelo cliente',
  correction_requested: 'Correção solicitada',
  analysis_started: 'Em análise',
  approved: 'Aprovado',
  resent: 'Formulário reenviado',
  created: 'Registro criado',
};

const ClientQualification = () => {
  const [activeTab, setActiveTab] = useState('admin');

  return (
    <div className="space-y-6" data-testid="client-qualification-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <UserCheck className="text-green-500" />
            Classificação de Clientes
          </h1>
          <p className="text-gray-400 mt-1">Documentos para Qualificação de Clientes</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab('admin')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'admin' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Administrativo
        </button>
        <button
          onClick={() => setActiveTab('send')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'send' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Enviar Classificação
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'documents' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Documentos Clientes
        </button>
      </div>

      {activeTab === 'admin' && <AdministrativoTab />}
      {activeTab === 'send' && <SendFormTab />}
      {activeTab === 'documents' && <ClientDocumentsTab />}
    </div>
  );
};

// ===================== ABA ADMINISTRATIVO =====================
const AdministrativoTab = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending_analysis: 0, in_analysis: 0, approved: 0, correction_needed: 0, draft: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [analysisItem, setAnalysisItem] = useState(null);

  useEffect(() => {
    loadData();
  }, [searchTerm, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsRes, statsRes] = await Promise.all([
        clientQualificationApi.list({ search: searchTerm, status: statusFilter }),
        clientQualificationApi.stats(),
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await clientQualificationApi.create(data);
      toast.success('Registro criado com sucesso');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar registro'));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await clientQualificationApi.update(editingItem.id, data);
      toast.success('Registro atualizado com sucesso');
      setShowModal(false);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;
    try {
      await clientQualificationApi.delete(id);
      toast.success('Registro excluído');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir registro');
    }
  };

  const handleResend = async (item) => {
    try {
      await clientQualificationApi.resendForm(item.id);
      toast.success(`Formulário reenviado para ${item.client_email}`);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao reenviar formulário'));
    }
  };

  const handleApprove = async (item) => {
    if (!window.confirm(`Aceitar a classificação do cliente "${item.fantasy_name || item.company_name || item.client_email || ''}"?`)) return;
    try {
      await clientQualificationApi.approve(item.id);
      toast.success('Classificação aceita com sucesso');
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao aceitar classificação'));
    }
  };

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (item) => <td className="font-mono text-white">{item.code}</td> },
    { key: 'cliente', label: 'Cliente', render: (item) => (
      <td className="text-white font-medium">
        {item.fantasy_name || '-'}
        {item.company_name && (
          <span className="block text-xs text-gray-500">{item.company_name}</span>
        )}
      </td>
    ) },
    { key: 'cnpj', label: 'CNPJ/CPF', render: (item) => <td className="text-gray-400">{item.cnpj || '-'}</td> },
    { key: 'email', label: 'E-mail', render: (item) => <td className="text-gray-400">{item.client_email || '-'}</td> },
    { key: 'status', label: 'Status', render: (item) => {
      const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
      return <td><span className={`px-2 py-1 rounded-full text-xs ${statusCfg.color}`}>{statusCfg.label}</span></td>;
    } },
    { key: 'created', label: 'Data da Solicitação', render: (item) => <td className="text-gray-400">{formatDateBR(item.created_at ? item.created_at.slice(0, 10) : null)}</td> },
    { key: 'responsavel', label: 'Responsável', render: (item) => <td className="text-gray-400">{item.requested_by_name || '-'}</td> },
    { key: 'anexos', label: 'Anexos', render: (item) => (
      <td>
        {item.attachments_count > 0 ? (
          <span className="flex items-center gap-1 text-green-400">
            <Paperclip size={14} />
            {item.attachments_count}
          </span>
        ) : (
          <span className="text-gray-600">-</span>
        )}
      </td>
    ) },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (item) => (
      <td>
        <div className="flex gap-1.5 justify-end">
          <button
            onClick={() => setAnalysisItem(item)}
            className="p-1.5 text-gray-400 hover:text-green-400 rounded"
            title="Analisar solicitação"
          >
            <Eye size={18} />
          </button>
          {item.status !== 'approved' && (
            <button
              onClick={() => handleApprove(item)}
              className="p-1.5 text-green-400 hover:text-green-300 rounded"
              title="Aceitar classificação"
            >
              <CheckCircle size={18} />
            </button>
          )}
          {item.status !== 'approved' && item.client_email && (
            <button
              onClick={() => handleResend(item)}
              className="p-1.5 text-gray-400 hover:text-blue-400 rounded"
              title="Reenviar formulário por e-mail"
            >
              <Mail size={18} />
            </button>
          )}
          <button
            onClick={() => { setEditingItem(item); setViewMode(false); setShowModal(true); }}
            className="p-1.5 text-gray-400 hover:text-blue-400 rounded"
            title="Editar"
          >
            <Edit2 size={18} />
          </button>
          <button
            onClick={() => handleDelete(item.id)}
            className="p-1.5 text-gray-400 hover:text-red-400 rounded"
            title="Excluir"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'client-qualification', columns: COLUMNS });

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Pendentes de Análise" value={stats.pending_analysis || 0} tone="yellow" />
        <StatCard label="Em Análise" value={stats.in_analysis || 0} tone="blue" />
        <StatCard label="Necessita Correção" value={stats.correction_needed || 0} tone="red" />
        <StatCard label="Aprovados" value={stats.approved || 0} tone="green" />
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-700">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome, razão social, CNPJ, código ou e-mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input px-3 w-full sm:w-60"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 pt-4">
              <p className="text-sm text-gray-400">
                {items.length} {items.length === 1 ? 'solicitação' : 'solicitações'}
              </p>
              <div className="flex items-center gap-2">
                <ExportButtons module="client_qualification" moduleName="Classificação de Clientes" filters={{}} />
                <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
                <button
                  onClick={() => { setEditingItem(null); setViewMode(false); setShowModal(true); }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus size={20} />
                  Novo Registro
                </button>
              </div>
            </div>

            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    {visibleColumns.map(col => (
                      <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} className="py-8 text-center text-gray-500">
                        Nenhuma solicitação encontrada
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.id}>
                        {visibleColumns.map(col => (
                          <Fragment key={col.key}>{col.render(item)}</Fragment>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal CRUD */}
      {showModal && (
        <ClientQualificationModal
          item={editingItem}
          readOnly={viewMode}
          onClose={() => { setShowModal(false); setEditingItem(null); setViewMode(false); }}
          onSave={editingItem ? handleUpdate : handleCreate}
        />
      )}

      {/* Modal de análise */}
      {analysisItem && (
        <AnalysisModal
          item={analysisItem}
          onClose={() => setAnalysisItem(null)}
          onReload={loadData}
        />
      )}
    </>
  );
};

const StatCard = ({ label, value, tone }) => {
  const tones = {
    yellow: 'text-yellow-400',
    blue: 'text-blue-400',
    red: 'text-red-400',
    green: 'text-green-400',
  };
  return (
    <div className="card p-4">
      <p className="text-gray-400 text-sm">{label}</p>
      <p className={`text-2xl font-bold ${tones[tone] || 'text-white'}`}>{value}</p>
    </div>
  );
};

// ===================== ABA ENVIAR CLASSIFICAÇÃO =====================
const SendFormTab = () => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [copyDone, setCopyDone] = useState(false);
  const [docsItem, setDocsItem] = useState(null);
  const [analysisItem, setAnalysisItem] = useState(null);

  useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    try {
      setLoadingDrafts(true);
      const res = await clientQualificationApi.list({ status: 'draft' });
      setDrafts(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar formulários enviados');
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const emailTrim = email.trim();
    if (!emailTrim) {
      toast.error('Informe o e-mail do cliente ou responsável');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.error('Informe um e-mail válido');
      return;
    }
    setSending(true);
    try {
      const res = await clientQualificationApi.sendForm(emailTrim);
      const item = res.data;
      const reqAnalysis = item.requirement_analysis || null;
      setLastSent({
        email: emailTrim,
        code: item.code,
        link: `${window.location.origin}/classificacao/${item.request_token}`,
        analysisCode: reqAnalysis?.code,
        analysisLink: reqAnalysis ? `${window.location.origin}/analise-critica/${reqAnalysis.request_token}` : null,
        emailSent: item.email_sent !== false,
        message: item.message,
      });
      setEmail('');
      setCopyDone(false);
      if (item.email_sent === false) {
        toast.warning(item.message || 'Não foi possível enviar o e-mail. Os links estão disponíveis abaixo.');
      } else {
        toast.success('Formulários enviados com sucesso');
      }
      loadDrafts();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao enviar formulário'));
    } finally {
      setSending(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopyDone(true);
      toast.success('Link copiado');
    }).catch(() => toast.error('Não foi possível copiar o link'));
  };

  const handleResend = async (item) => {
    try {
      await clientQualificationApi.resendForm(item.id);
      toast.success(`Formulário reenviado para ${item.client_email}`);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao reenviar formulário'));
    }
  };

  const getLink = (item) => `${window.location.origin}/classificacao/${item.request_token}`;

  return (
    <div className="space-y-6">
      {/* Enviar formulário */}
      <div className="card p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center">
            <Send size={20} className="text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Enviar formulário de classificação</h2>
            <p className="text-sm text-gray-400">
              O cliente receberá um link exclusivo por e-mail para preencher o formulário e anexar os documentos.
            </p>
          </div>
        </div>

        <form onSubmit={handleSend} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="email"
              placeholder="E-mail do cliente ou responsável"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <button type="submit" className="btn-primary flex items-center gap-2 justify-center" disabled={sending}>
            {sending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
            Enviar formulário
          </button>
        </form>

        {lastSent && (
          <div className={`mt-4 p-4 rounded-lg border ${lastSent.emailSent ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
            <p className={`font-medium flex items-center gap-2 ${lastSent.emailSent ? 'text-green-400' : 'text-yellow-400'}`}>
              {lastSent.emailSent ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              {lastSent.emailSent
                ? `Formulário enviado para ${lastSent.email}`
                : `E-mail não enviado — ${lastSent.message || 'copie o link e envie manualmente'}`}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Solicitação <span className="text-green-400 font-mono">{lastSent.code}</span>
              {lastSent.analysisCode && (
                <> e análise <span className="text-green-400 font-mono">{lastSent.analysisCode}</span></>
              )}
              . Compartilhe os links abaixo caso o cliente não receba o e-mail:
            </p>
            <div className="mt-2 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <input
                  readOnly
                  value={lastSent.link}
                  onFocus={(e) => e.target.select()}
                  className="input w-full text-xs font-mono"
                />
                <button
                  type="button"
                  onClick={() => copyLink(lastSent.link)}
                  className="btn-secondary flex items-center gap-2 whitespace-nowrap"
                >
                  {copyDone ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                  Copiar link
                </button>
              </div>
              {lastSent.analysisLink && (
                <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  <input
                    readOnly
                    value={lastSent.analysisLink}
                    onFocus={(e) => e.target.select()}
                    className="input w-full text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => copyLink(lastSent.analysisLink)}
                    className="btn-secondary flex items-center gap-2 whitespace-nowrap"
                  >
                    {copyDone ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                    Copiar link
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Links enviados aguardando preenchimento */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
            <Clock size={16} className="text-green-500" />
            Formulários enviados aguardando preenchimento
          </h3>
          <span className="badge badge-gray">{drafts.length}</span>
        </div>
        {loadingDrafts ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-green-500" size={28} />
          </div>
        ) : drafts.length === 0 ? (
          <p className="p-6 text-center text-gray-500 text-sm">Nenhum formulário pendente de preenchimento</p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>E-mail</th>
                  <th>Data de Envio</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((item) => (
                  <tr key={item.id}>
                    <td className="font-mono text-white">{item.code}</td>
                    <td className="text-gray-400">{item.client_email}</td>
                    <td className="text-gray-400">{formatDateBR(item.created_at ? item.created_at.slice(0, 10) : null)}</td>
                    <td>
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">
                        Aguardando preenchimento
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1.5 justify-end">
                        <button
                          onClick={() => setAnalysisItem(item)}
                          className="p-1.5 text-gray-400 hover:text-green-400 rounded"
                          title="Revisar formulário"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => setDocsItem(item)}
                          className="p-1.5 text-gray-400 hover:text-green-400 rounded"
                          title="Ver documentos anexados"
                        >
                          <Paperclip size={18} />
                        </button>
                        <button
                          onClick={() => copyLink(getLink(item))}
                          className="p-1.5 text-gray-400 hover:text-green-400 rounded"
                          title="Copiar link"
                        >
                          <Link2 size={18} />
                        </button>
                        <button
                          onClick={() => handleResend(item)}
                          className="p-1.5 text-gray-400 hover:text-blue-400 rounded"
                          title="Reenviar formulário por e-mail"
                        >
                          <Mail size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de documentos anexados */}
      {docsItem && (
        <DraftDocumentsModal item={docsItem} onClose={() => setDocsItem(null)} />
      )}

      {/* Modal de revisão do formulário */}
      {analysisItem && (
        <AnalysisModal
          item={analysisItem}
          onClose={() => setAnalysisItem(null)}
          onReload={loadDrafts}
        />
      )}
    </div>
  );
};

// ===================== MODAL DE DOCUMENTOS ANEXADOS (aba Enviar Classificação) =====================
const DraftDocumentsModal = ({ item, onClose }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await clientQualificationApi.listAttachments(item.id);
        setAttachments(res.data || []);
      } catch (error) {
        toast.error('Erro ao carregar documentos anexados');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [item.id]);

  const handleDownload = async (att) => {
    setDownloadingId(att.id);
    try {
      const res = await clientQualificationApi.downloadAttachment(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', att.file_name || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao baixar documento'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content p-6 max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Paperclip size={18} className="text-green-500" /> Documentos anexados
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">
              <span className="font-mono text-green-400">{item.code}</span> · {item.client_email}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={22} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-green-500" size={28} />
          </div>
        ) : attachments.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">Nenhum documento anexado ainda</p>
        ) : (
          <div className="space-y-2">
            {attachments.map((att) => (
              <div key={att.id} className="flex items-center justify-between gap-2 border border-gray-700 rounded-lg px-3 py-2 bg-gray-800/40">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-green-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-200 truncate">{att.file_name}</p>
                    <p className="text-xs text-gray-500">
                      {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''}
                      {att.uploaded_at ? ` · ${new Date(att.uploaded_at).toLocaleDateString('pt-BR')}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(att)}
                  className="p-1.5 text-blue-400 hover:text-blue-300 flex-shrink-0"
                  title="Baixar"
                >
                  {downloadingId === att.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ===================== FORMULÁRIO DE CLASSIFICAÇÃO (campos) =====================
export const QualificationFormFields = ({ formData, setFormData, readOnly = false, errors = {}, onClearError }) => {
  const setDoc = (key, val) => setFormData((f) => ({ ...f, documents: { ...f.documents, [key]: val } }));

  const toggleProductClass = (key) => {
    setFormData((f) => ({
      ...f,
      product_classes: f.product_classes.includes(key)
        ? f.product_classes.filter((c) => c !== key)
        : [...f.product_classes, key],
    }));
  };

  return (
    <fieldset disabled={readOnly} className="space-y-6 min-w-0">
      {/* Dados da Empresa */}
      <div className="space-y-3">
        <SectionTitle>Dados da Empresa</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Nome Fantasia *</label>
            <input
              id="fq-fantasy_name"
              type="text"
              value={formData.fantasy_name}
              onChange={(e) => {
                setFormData({ ...formData, fantasy_name: e.target.value });
                onClearError?.('fantasy_name');
              }}
              className={`input w-full ${errors.fantasy_name ? 'border-red-500' : ''}`}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Razão Social *</label>
            <input
              id="fq-company_name"
              type="text"
              value={formData.company_name}
              onChange={(e) => {
                setFormData({ ...formData, company_name: e.target.value });
                onClearError?.('company_name');
              }}
              className={`input w-full ${errors.company_name ? 'border-red-500' : ''}`}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">CNPJ/CPF</label>
            <input
              type="text"
              value={formData.cnpj}
              onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
              className="input w-full"
              maxLength={255}
              placeholder="CNPJ ou CPF"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">Endereço</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="input w-full"
            />
          </div>
        </div>
      </div>

      {/* Dados de Contato */}
      <div className="space-y-3">
        <SectionTitle>Dados de Contato</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">E-mails Departamento da Qualidade</label>
            <input
              type="text"
              value={formData.quality_email}
              onChange={(e) => setFormData({ ...formData, quality_email: e.target.value })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">E-mails para envio de Faturamento</label>
            <input
              type="text"
              value={formData.billing_email}
              onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">E-mails para cobrança</label>
            <input
              type="text"
              value={formData.collection_email}
              onChange={(e) => setFormData({ ...formData, collection_email: e.target.value })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">E-mails para recebimento de DACTE</label>
            <input
              type="text"
              value={formData.dacte_email}
              onChange={(e) => setFormData({ ...formData, dacte_email: e.target.value })}
              className="input w-full"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-6">
            <span className="text-sm text-gray-300">Deseja receber o XML do DACTE na emissão?</span>
            {[[true, 'SIM'], [false, 'NÃO']].map(([val, text]) => (
              <label key={text} className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                <input
                  type="radio"
                  checked={formData.wants_dacte_xml === val}
                  onChange={() => setFormData({ ...formData, wants_dacte_xml: val })}
                  className="form-radio text-green-500"
                />
                {text}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Tipo de Serviço */}
      <div className="space-y-3">
        <SectionTitle>Tipo de Serviço</SectionTitle>
        <div className="flex flex-col sm:flex-row gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.service_transport}
              onChange={(e) => setFormData({ ...formData, service_transport: e.target.checked })}
              className="form-checkbox text-green-500"
            />
            Serviço de transporte
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.service_storage}
              onChange={(e) => setFormData({ ...formData, service_storage: e.target.checked })}
              className="form-checkbox text-green-500"
            />
            Serviço de Armazenagem
          </label>
        </div>
      </div>

      {/* Checklist de Documentos */}
      <div className="space-y-1">
        <SectionTitle>1 - A empresa possui os documentos especificados abaixo? Enviar uma cópia simples.</SectionTitle>
        <div className="px-1">
          {DOCUMENT_ITEMS.map((doc) => (
            <TriStateRow
              key={doc.key}
              label={doc.label}
              value={formData.documents[doc.key]}
              onChange={(val) => setDoc(doc.key, val)}
            />
          ))}
        </div>
      </div>

      {/* Produtos Perigosos */}
      <div className="space-y-1">
        <SectionTitle>Produtos Perigosos</SectionTitle>
        <div className="px-1">
          <TriStateRow
            label="Possui produtos perigosos?"
            value={formData.has_hazardous_products}
            onChange={(val) => setFormData({ ...formData, has_hazardous_products: val })}
          />
          <TriStateRow
            label="Se houver produtos perigosos, encaminhar as FDS's e Fichas de Emergência"
            value={formData.hazmat_fds_sent}
            onChange={(val) => setFormData({ ...formData, hazmat_fds_sent: val })}
          />
          <TriStateRow
            label="Rótulo de perigo conforme GHS - Classificação e Rotulagem de Produtos Químicos e Número ONU"
            value={formData.hazmat_ghs_label}
            onChange={(val) => setFormData({ ...formData, hazmat_ghs_label: val })}
          />
          <TriStateRow
            label="A operação necessita Envelope para o Transporte (FDS, ficha de emergência e telefone do responsável)"
            value={formData.hazmat_emergency_envelope}
            onChange={(val) => setFormData({ ...formData, hazmat_emergency_envelope: val })}
          />
        </div>
      </div>

      {/* Transporte / Classe de Produtos */}
      <div className="space-y-3">
        <SectionTitle>Transporte e Classe de Produtos</SectionTitle>
        <div className="px-1">
          <TriStateRow
            label="O tipo de transporte contratado é de acordo com a ANTT (Resolução nº 5.947/21)?"
            value={formData.transport_antt_compliant}
            onChange={(val) => setFormData({ ...formData, transport_antt_compliant: val })}
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 px-1">
          {PRODUCT_CLASSES.map((pc) => (
            <label key={pc.key} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.product_classes.includes(pc.key)}
                onChange={() => toggleProductClass(pc.key)}
                className="form-checkbox text-green-500"
              />
              {pc.label}
            </label>
          ))}
        </div>
        {formData.product_classes.includes('outros') && (
          <input
            type="text"
            value={formData.product_classes_other}
            onChange={(e) => setFormData({ ...formData, product_classes_other: e.target.value })}
            className="input w-full"
            placeholder="Descreva a classe 'Outros'"
          />
        )}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Lista de produtos das classes selecionadas, com seus números de registro
          </label>
          <textarea
            value={formData.product_registrations}
            onChange={(e) => setFormData({ ...formData, product_registrations: e.target.value })}
            className="input w-full"
            rows={2}
          />
        </div>
      </div>

      {/* Preenchimento - Place */}
      <div className="space-y-3">
        <SectionTitle>Preenchimento - Place</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Vendedor Responsável</label>
            <input
              type="text"
              value={formData.seller_responsible}
              onChange={(e) => setFormData({ ...formData, seller_responsible: e.target.value })}
              className="input w-full"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Foi negociado com o cliente alguma condição especial de faturamento? Caso sim, informe qual.
            </label>
            <textarea
              value={formData.special_billing_condition}
              onChange={(e) => setFormData({ ...formData, special_billing_condition: e.target.value })}
              className="input w-full"
              rows={2}
            />
          </div>
        </div>
      </div>

      {/* Observações */}
      <div className="space-y-3">
        <SectionTitle>Observações</SectionTitle>
        <textarea
          value={formData.observations}
          onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
          className="input w-full"
          rows={2}
        />
      </div>
    </fieldset>
  );
};

export const buildFormData = (item) => ({
  fantasy_name: item?.fantasy_name || '',
  company_name: item?.company_name || '',
  cnpj: item?.cnpj || '',
  address: item?.address || '',
  quality_email: item?.quality_email || '',
  billing_email: item?.billing_email || '',
  collection_email: item?.collection_email || '',
  wants_dacte_xml: item?.wants_dacte_xml ?? null,
  dacte_email: item?.dacte_email || '',
  service_transport: item?.service_transport ?? false,
  service_storage: item?.service_storage ?? false,
  documents: item?.documents || {},
  has_hazardous_products: item?.has_hazardous_products || '',
  hazmat_fds_sent: item?.hazmat_fds_sent || '',
  hazmat_ghs_label: item?.hazmat_ghs_label || '',
  hazmat_emergency_envelope: item?.hazmat_emergency_envelope || '',
  transport_antt_compliant: item?.transport_antt_compliant || '',
  product_classes: item?.product_classes || [],
  product_classes_other: item?.product_classes_other || '',
  product_registrations: item?.product_registrations || '',
  seller_responsible: item?.seller_responsible || '',
  special_billing_condition: item?.special_billing_condition || '',
  quality_evaluation: item?.quality_evaluation || '',
  quality_evaluation_reason: item?.quality_evaluation_reason || '',
  quality_evaluation_date: item?.quality_evaluation_date || '',
  quality_responsible: item?.quality_responsible || '',
  quality_responsible_date: item?.quality_responsible_date || '',
  observations: item?.observations || '',
});

// ===================== MODAL CRUD (manual) =====================
const ClientQualificationModal = ({ item, readOnly = false, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(() => buildFormData(item));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([
      { value: formData.quality_evaluation_date, label: 'Data da Avaliação' },
      { value: formData.quality_responsible_date, label: 'Data do Responsável' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    if (formData.quality_evaluation === 'reprovado' && !formData.quality_evaluation_reason) {
      toast.error('Informe o motivo da reprovação');
      return;
    }
    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.quality_evaluation_date) delete data.quality_evaluation_date;
      if (!data.quality_responsible_date) delete data.quality_responsible_date;
      if (!data.quality_evaluation) delete data.quality_evaluation;
      if (!data.has_hazardous_products) delete data.has_hazardous_products;
      if (!data.hazmat_fds_sent) delete data.hazmat_fds_sent;
      if (!data.hazmat_ghs_label) delete data.hazmat_ghs_label;
      if (!data.hazmat_emergency_envelope) delete data.hazmat_emergency_envelope;
      if (!data.transport_antt_compliant) delete data.transport_antt_compliant;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {readOnly ? 'Visualizar Registro' : item ? 'Editar Registro' : 'Novo Registro de Classificação'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <QualificationFormFields formData={formData} setFormData={setFormData} readOnly={readOnly} />

          <div className="space-y-3">
            <SectionTitle>Avaliação da Qualidade - Preenchimento Place</SectionTitle>
            <div className="flex flex-wrap gap-6">
              {[['aprovado', 'Aprovado'], ['aprovado_ressalvas', 'Aprovado c/ ressalvas'], ['reprovado', 'Reprovado']].map(([val, text]) => (
                <label key={val} className="flex items-center gap-1.5 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.quality_evaluation === val}
                    onChange={() => setFormData({ ...formData, quality_evaluation: val })}
                    className="form-radio text-green-500"
                    disabled={readOnly}
                  />
                  {text}
                </label>
              ))}
            </div>
            {formData.quality_evaluation === 'reprovado' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Motivo *</label>
                <input
                  type="text"
                  value={formData.quality_evaluation_reason}
                  onChange={(e) => setFormData({ ...formData, quality_evaluation_reason: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data da Avaliação</label>
                <input
                  type="date"
                  value={formData.quality_evaluation_date}
                  onChange={(e) => setFormData({ ...formData, quality_evaluation_date: e.target.value })}
                  className="input w-full"
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
                <input
                  type="text"
                  value={formData.quality_responsible}
                  onChange={(e) => setFormData({ ...formData, quality_responsible: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data do Responsável</label>
                <input
                  type="date"
                  value={formData.quality_responsible_date}
                  onChange={(e) => setFormData({ ...formData, quality_responsible_date: e.target.value })}
                  className="input w-full"
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              {readOnly ? 'Fechar' : 'Cancelar'}
            </button>
            {!readOnly && (
              <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

// ===================== MODAL DE ANÁLISE (admin) =====================
const AnalysisModal = ({ item, onClose, onReload }) => {
  const [detail, setDetail] = useState(item);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(() => buildFormData(item));
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [observation, setObservation] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [analysisDetail, setAnalysisDetail] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisFormData, setAnalysisFormData] = useState(null);
  const [formTab, setFormTab] = useState('qualification');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await clientQualificationApi.get(item.id);
        setDetail(res.data);
        setFormData(buildFormData(res.data));
        if (res.data.requirement_analysis_id) {
          setAnalysisLoading(true);
          try {
            const analysisRes = await clientRequirementAnalysisApi.get(res.data.requirement_analysis_id);
            setAnalysisDetail(analysisRes.data);
            setAnalysisFormData(buildRequirementFormData(analysisRes.data));
          } catch (error) {
            toast.error('Erro ao carregar Análise e Crítica dos Requisitos');
          } finally {
            setAnalysisLoading(false);
          }
        } else {
          setAnalysisDetail(null);
          setAnalysisFormData(null);
        }
      } catch (error) {
        toast.error('Erro ao carregar detalhes');
      } finally {
        setLoading(false);
      }
      // Auto-marcar como em análise
      try {
        await clientQualificationApi.startAnalysis(item.id);
      } catch (error) {
        // silencioso: já em análise ou aprovado
      }
    };
    load();
  }, [item.id]);

  const statusCfg = STATUS_CONFIG[detail.status] || STATUS_CONFIG.draft;

  const handleApprove = async () => {
    if (!window.confirm('Confirmar a aprovação desta classificação?')) return;
    setActionLoading(true);
    try {
      await clientQualificationApi.approve(detail.id);
      toast.success('Classificação aprovada com sucesso');
      onReload();
      onClose();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao aprovar'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!observation.trim()) {
      toast.error('A observação é obrigatória para solicitar correção');
      return;
    }
    setActionLoading(true);
    try {
      await clientQualificationApi.reject(detail.id, observation.trim());
      toast.success('Correção solicitada. A solicitação retornou para o cliente.');
      onReload();
      onClose();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao solicitar correção'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownload = async (att) => {
    setDownloadingId(att.id);
    try {
      const res = await clientQualificationApi.downloadAttachment(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', att.file_name || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao baixar arquivo'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleAnalysisDownload = async (att) => {
    setDownloadingId(att.id);
    try {
      const res = await clientRequirementAnalysisApi.downloadAttachment(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', att.file_name || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao baixar arquivo'));
    } finally {
      setDownloadingId(null);
    }
  };

  const attachments = detail.attachments || [];

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content p-6 max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <UserCheck className="text-green-500" />
              Análise de Classificação
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-green-400">{detail.code}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${statusCfg.color}`}>{statusCfg.label}</span>
              <span className="text-xs text-gray-500">
                Solicitação: {formatDateBR(detail.created_at ? detail.created_at.slice(0, 10) : null)}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Informações da solicitação */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <InfoBox label="Cliente" value={detail.fantasy_name || '-'} />
          <InfoBox label="E-mail" value={detail.client_email || '-'} />
          <InfoBox label="Responsável" value={detail.requested_by_name || '-'} />
          <InfoBox label="Reenvios" value={String(detail.review_count || 0)} />
        </div>

        {/* Formulários vinculados enviados no mesmo e-mail */}
        {analysisDetail && !loading && (
          <div className="flex gap-2 border-b border-gray-700 mb-4 overflow-x-auto">
            <button
              onClick={() => setFormTab('qualification')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                formTab === 'qualification' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              Classificação de Clientes
            </button>
            <button
              onClick={() => setFormTab('analysis')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                formTab === 'analysis' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              Análise e Crítica
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-green-500" size={28} />
          </div>
        ) : (
          <>
            {formTab === 'qualification' && (
              <>
            {/* Andamento da solicitação */}
            {(detail.timeline || []).length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide bg-gray-800/60 px-3 py-2 rounded mb-3 flex items-center gap-2">
                  <History size={16} /> Andamento da solicitação
                </h3>
                <div className="space-y-0 pl-2">
                  {(detail.timeline || []).map((ev, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full mt-1 ${idx === (detail.timeline || []).length - 1 ? 'bg-green-500' : 'bg-gray-600'}`} />
                        {idx < (detail.timeline || []).length - 1 && <div className="w-px flex-1 bg-gray-700" />}
                      </div>
                      <div className="pb-4">
                        <p className="text-sm text-gray-200">{ev.message}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(ev.date).toLocaleString('pt-BR')}
                          {STATUS_STEP_LABELS[ev.type] ? ` · ${STATUS_STEP_LABELS[ev.type]}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documentos enviados */}
            <div className="mb-4">
              <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide bg-gray-800/60 px-3 py-2 rounded mb-3 flex items-center gap-2">
                <Paperclip size={16} /> Documentos enviados ({attachments.length})
              </h3>
              {attachments.length === 0 ? (
                <p className="text-sm text-gray-500 px-3">Nenhum documento anexado</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-1">
                  {attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between gap-2 border border-gray-700 rounded-lg px-3 py-2 bg-gray-800/40">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={16} className="text-green-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-gray-200 truncate">{att.file_name}</p>
                          <p className="text-xs text-gray-500">
                            {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''} · {new Date(att.uploaded_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(att)}
                        className="p-1.5 text-blue-400 hover:text-blue-300 flex-shrink-0"
                        title="Baixar"
                      >
                        {downloadingId === att.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Dados preenchidos */}
            {detail.status === 'draft' ? (
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <p className="text-yellow-400 font-medium flex items-center gap-2 mb-1">
                  <Clock size={16} /> Formulário ainda não preenchido
                </p>
                <p className="text-sm text-gray-300">
                  O cliente ainda não preencheu e enviou este formulário pelo link. Nenhum dado foi recebido até o momento.
                </p>
              </div>
            ) : (
              <QualificationFormFields formData={formData} setFormData={setFormData} readOnly />
            )}

            {detail.admin_observation && (
              <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <p className="text-red-400 font-medium flex items-center gap-2 mb-1">
                  <AlertCircle size={16} /> Observação da solicitação de correção
                </p>
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{detail.admin_observation}</p>
              </div>
            )}
              </>
            )}
            {formTab === 'analysis' && (
              <>
                {analysisLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="animate-spin text-green-500" size={28} />
                  </div>
                ) : analysisDetail ? (
                  <>
                    {analysisDetail.status === 'draft' ? (
                      <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                        <p className="text-yellow-400 font-medium flex items-center gap-2 mb-1">
                          <Clock size={16} /> Formulário ainda não preenchido
                        </p>
                        <p className="text-sm text-gray-300">
                          O cliente ainda não preencheu e enviou o formulário de Análise e Crítica pelo link. Nenhum dado foi recebido até o momento.
                        </p>
                      </div>
                    ) : (
                      <RequirementAnalysisFormFields formData={analysisFormData} readOnly />
                    )}

                    <div className="mt-4">
                      <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide bg-gray-800/60 px-3 py-2 rounded mb-3 flex items-center gap-2">
                        <Paperclip size={16} /> Documentos enviados ({(analysisDetail.attachments || []).length})
                      </h3>
                      {(analysisDetail.attachments || []).length === 0 ? (
                        <p className="text-sm text-gray-500 px-3">Nenhum documento anexado</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-1">
                          {(analysisDetail.attachments || []).map((att) => (
                            <div key={att.id} className="flex items-center justify-between gap-2 border border-gray-700 rounded-lg px-3 py-2 bg-gray-800/40">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText size={16} className="text-green-500 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm text-gray-200 truncate">{att.file_name}</p>
                                  <p className="text-xs text-gray-500">
                                    {att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''}
                                    {att.uploaded_at ? ` · ${new Date(att.uploaded_at).toLocaleDateString('pt-BR')}` : ''}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleAnalysisDownload(att)}
                                className="p-1.5 text-blue-400 hover:text-blue-300 flex-shrink-0"
                                title="Baixar"
                              >
                                {downloadingId === att.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {analysisDetail.admin_observation && (
                      <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                        <p className="text-red-400 font-medium flex items-center gap-2 mb-1">
                          <AlertCircle size={16} /> Observação da solicitação de correção
                        </p>
                        <p className="text-sm text-gray-300 whitespace-pre-wrap">{analysisDetail.admin_observation}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500 py-6 text-center">
                    Nenhum registro de Análise e Crítica vinculado.
                  </p>
                )}
              </>
            )}
          </>
        )}

        {/* Ações */}
        <div className="mt-6 pt-4 border-t border-gray-700">
          {!showRejectForm ? (
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              {detail.status !== 'approved' && (
                <>
                  <button
                    onClick={() => setShowRejectForm(true)}
                    disabled={actionLoading || loading}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <ThumbsDown size={18} />
                    Reprovar / Solicitar correção
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={actionLoading || loading}
                    className="btn-primary flex items-center gap-2 bg-green-600"
                  >
                    {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <ThumbsUp size={18} />}
                    Aprovar
                  </button>
                </>
              )}
              {detail.status === 'approved' && (
                <span className="text-green-400 font-medium flex items-center gap-2 justify-end">
                  <CheckCircle size={18} /> Classificação aprovada
                </span>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Observação (obrigatória) <span className="text-red-400">*</span>
              </label>
              <textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                className="input w-full"
                rows={3}
                placeholder="Informe o que está faltando ou precisa ser corrigido..."
                autoFocus
              />
              <div className="flex gap-3 justify-end mt-3">
                <button onClick={() => setShowRejectForm(false)} className="btn-secondary" disabled={actionLoading}>
                  Cancelar
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading || !observation.trim()}
                  className="btn-primary flex items-center gap-2 bg-red-600"
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <MessageSquare size={18} />}
                  Enviar solicitação de correção
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const InfoBox = ({ label, value }) => (
  <div className="border border-gray-700 rounded-lg p-3 bg-gray-800/40">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm text-gray-200 truncate" title={value}>{value}</p>
  </div>
);

// Tri-state SIM / NÃO / N.A. row, mirroring the paper form's checklist columns
export const TriStateRow = ({ label, value, onChange }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-2 border-b border-gray-800">
    <span className="flex-1 text-sm text-gray-300">{label}</span>
    <div className="flex gap-4">
      {[['sim', 'SIM'], ['nao', 'NÃO'], ['na', 'N.A.']].map(([val, text]) => (
        <label key={val} className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
          <input
            type="radio"
            checked={value === val}
            onChange={() => onChange(val)}
            className="form-radio text-green-500"
          />
          {text}
        </label>
      ))}
    </div>
  </div>
);

export const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-green-400 uppercase tracking-wide bg-gray-800/60 px-3 py-2 rounded">
    {children}
  </h3>
);

// ===================== ABA DOCUMENTOS CLIENTES =====================
const DOC_STORAGE_LABELS = {
  year: 'Por Ano',
  certificate: 'Por Certificado',
};

const DOC_RETENTION_LABELS = {
  annual: 'Anual',
  biennial: 'Bienal',
};

const DOC_DISPOSAL_LABELS = {
  annual: 'Anual',
  monthly: 'Mensal',
  semiannual: 'Semestral',
  quarterly: 'Trimestral',
  valid: 'Vigente',
};

const ClientDocumentsTab = () => {
  const [groups, setGroups] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [stats, setStats] = useState({ total: 0, expired: 0, due_soon: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [expiredOnly, setExpiredOnly] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  useEffect(() => {
    loadGroups();
    loadSectors();
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [selectedGroupId, expiredOnly]);

  const loadGroups = async () => {
    try {
      const res = await clientDocumentsApi.listGroups();
      setGroups(res.data || []);
      if (res.data?.length > 0 && !selectedGroupId) {
        setSelectedGroupId(res.data[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    }
  };

  const loadSectors = async () => {
    try {
      const res = await sectorsApi.listActive();
      setSectors(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = { expired_only: expiredOnly || undefined };
      if (selectedGroupId) params.group_id = selectedGroupId;
      const [docsRes, statsRes] = await Promise.all([
        clientDocumentsApi.list(params),
        clientDocumentsApi.stats(),
      ]);
      setDocuments(docsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (data) => {
    try {
      await clientDocumentsApi.createGroup(data);
      toast.success('Cliente criado com sucesso');
      setShowGroupModal(false);
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar cliente'));
    }
  };

  const handleUpdateGroup = async (data) => {
    try {
      await clientDocumentsApi.updateGroup(editingGroup.id, data);
      toast.success('Cliente atualizado com sucesso');
      setShowGroupModal(false);
      setEditingGroup(null);
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar cliente'));
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Excluir o cliente "${group.name}"? Os documentos do cliente também serão removidos.`)) return;
    try {
      await clientDocumentsApi.deleteGroup(group.id);
      toast.success('Cliente excluído');
      setSelectedGroupId('');
      await loadGroups();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir cliente'));
    }
  };

  const handleCreateDocument = async (data) => {
    try {
      await clientDocumentsApi.create({ ...data, group_id: selectedGroupId });
      toast.success('Documento criado com sucesso');
      setShowDocModal(false);
      loadDocuments();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar documento'));
    }
  };

  const handleUpdateDocument = async (data) => {
    try {
      await clientDocumentsApi.update(editingDoc.id, data);
      toast.success('Documento atualizado com sucesso');
      setShowDocModal(false);
      setEditingDoc(null);
      loadDocuments();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar documento'));
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;
    try {
      await clientDocumentsApi.delete(id);
      toast.success('Documento excluído');
      loadDocuments();
    } catch (error) {
      toast.error('Erro ao excluir documento');
    }
  };

  const handleDownloadFile = async (doc) => {
    if (!doc.has_file) return;
    setDownloadingId(doc.id);
    try {
      const res = await clientDocumentsApi.downloadFile(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || `documento_${doc.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao baixar arquivo'));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleUploadFile = async (doc, file) => {
    if (!file) return;
    setUploadingId(doc.id);
    try {
      await clientDocumentsApi.uploadFile(doc.id, file);
      toast.success('Arquivo anexado com sucesso');
      loadDocuments();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao anexar arquivo'));
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteFile = async (doc) => {
    setUploadingId(doc.id);
    try {
      await clientDocumentsApi.deleteFile(doc.id);
      toast.success('Arquivo removido');
      loadDocuments();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao remover arquivo'));
    } finally {
      setUploadingId(null);
    }
  };

  const filtered = searchTerm
    ? documents.filter((d) =>
        [d.name, d.edition, d.area_name, ...(d.access_area_names || [])]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : documents;

  const filteredGroups = groupSearch
    ? groups.filter((g) =>
        [g.name, g.description].filter(Boolean).some((v) => String(v).toLowerCase().includes(groupSearch.toLowerCase()))
      )
    : groups;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-3">
            <FolderOpen className="text-green-500" />
            Documentos Clientes
          </h2>
          <p className="text-gray-400 mt-1 text-sm">Controle de documentos de clientes e seus vencimentos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEditingGroup(null); setShowGroupModal(true); }} className="btn-secondary flex items-center gap-2">
            <Plus size={20} />
            Novo Cliente
          </button>
          <button onClick={() => { setEditingDoc(null); setShowDocModal(true); }} className="btn-primary flex items-center gap-2" disabled={!selectedGroupId}>
            <Plus size={20} />
            Novo Documento
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total de Documentos</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencidos</p>
          <p className="text-2xl font-bold text-red-400">{stats.expired}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencem neste mês</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.due_soon}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Clientes</p>
          <p className="text-2xl font-bold text-white">{groups.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Groups sidebar */}
        <div className="card overflow-hidden lg:col-span-1">
          <div className="p-3 border-b border-gray-700 flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Layers size={16} className="text-green-500" />
              Clientes
            </span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Filtrar clientes..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                className="input pl-8 pr-2 py-1.5 w-full text-xs"
              />
            </div>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {filteredGroups.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 text-center">
                {groups.length === 0 ? 'Nenhum cliente criado' : 'Nenhum cliente encontrado'}
              </p>
            ) : (
              filteredGroups.map((group) => (
                <div
                  key={group.id}
                  onClick={() => setSelectedGroupId(group.id)}
                  className={`flex items-center justify-between px-3 py-3 cursor-pointer border-b border-gray-800 transition-colors ${
                    selectedGroupId === group.id ? 'bg-green-500/10 border-l-2 border-l-green-500' : 'hover:bg-gray-800/40'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{group.name}</p>
                    {group.description && (
                      <p className="text-xs text-gray-500 truncate">{group.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => { setEditingGroup(group); setShowGroupModal(true); }}
                      className="p-1 text-gray-400 hover:text-blue-400"
                      title="Editar cliente"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      className="p-1 text-gray-400 hover:text-red-400"
                      title="Excluir cliente"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Documents table */}
        <div className="card overflow-hidden lg:col-span-3">
          <div className="p-3 border-b border-gray-700 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar documento, edição, área..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
            <label className="flex items-center gap-2 text-gray-400">
              <input
                type="checkbox"
                checked={expiredOnly}
                onChange={(e) => setExpiredOnly(e.target.checked)}
                className="form-checkbox text-green-500"
              />
              Apenas Vencidos
            </label>
          </div>

          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Edição</th>
                  <th>Área</th>
                  <th>Armazenamento</th>
                  <th>Retenção</th>
                  <th>Disposição</th>
                  <th>Vencimento</th>
                  <th>Dias p/ Vencer</th>
                  <th>Situação</th>
                  <th>Anexo</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="py-12 text-center">
                      <Loader2 className="animate-spin text-green-500 inline" size={28} />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="py-12 text-center text-gray-500">
                      {selectedGroupId ? 'Nenhum documento encontrado neste cliente' : 'Selecione um cliente para ver os documentos'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((doc) => (
                    <tr key={doc.id}>
                      <td className="text-white font-medium">
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-green-500 flex-shrink-0" />
                          <span
                            title={
                              doc.access_area_names?.length > 0
                                ? `Áreas com acesso: ${doc.access_area_names.join(', ')}`
                                : 'Acesso: todos os setores'
                            }
                          >
                            {doc.name}
                          </span>
                        </div>
                      </td>
                      <td className="text-gray-400">{doc.edition || '-'}</td>
                      <td className="text-gray-400">
                        {doc.area_name ? (
                          <span className="flex items-center gap-1">
                            <Building2 size={13} className="text-gray-500" />
                            {doc.area_name}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="text-gray-400">{DOC_STORAGE_LABELS[doc.storage_type] || doc.storage_type || '-'}</td>
                      <td className="text-gray-400">{DOC_RETENTION_LABELS[doc.retention_min] || doc.retention_min || '-'}</td>
                      <td className="text-gray-400">{DOC_DISPOSAL_LABELS[doc.disposal] || doc.disposal || '-'}</td>
                      <td className="text-gray-400">{formatDateBR(doc.expiration_date)}</td>
                      <td>
                        {doc.days_to_expire == null ? (
                          <span className="text-gray-500">-</span>
                        ) : doc.days_to_expire < 0 ? (
                          <span className="text-red-400 font-medium">
                            Vencido há {Math.abs(doc.days_to_expire)} {Math.abs(doc.days_to_expire) === 1 ? 'dia' : 'dias'}
                          </span>
                        ) : doc.days_to_expire <= 30 ? (
                          <span className="text-yellow-400 font-medium">
                            {doc.days_to_expire} {doc.days_to_expire === 1 ? 'dia' : 'dias'}
                          </span>
                        ) : (
                          <span className="text-green-400 font-medium">
                            {doc.days_to_expire} {doc.days_to_expire === 1 ? 'dia' : 'dias'}
                          </span>
                        )}
                      </td>
                      <td>
                        {doc.is_expired ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
                            <AlertCircle size={12} />
                            Vencido
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
                            <CheckCircle size={12} />
                            Vigente
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {doc.has_file ? (
                            <>
                              <button
                                onClick={() => handleDownloadFile(doc)}
                                className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                                title="Baixar anexo"
                              >
                                {downloadingId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                              </button>
                              <button
                                onClick={() => handleDeleteFile(doc)}
                                disabled={uploadingId === doc.id}
                                className="text-red-400 hover:text-red-300"
                                title="Remover anexo"
                              >
                                {uploadingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                              </button>
                            </>
                          ) : (
                            <label
                              className="flex items-center gap-1 text-xs text-gray-400 hover:text-white cursor-pointer"
                              title="Anexar arquivo"
                            >
                              {uploadingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                              Anexar
                              <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt"
                                style={{ display: 'none' }}
                                disabled={uploadingId === doc.id}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = '';
                                  if (f) handleUploadFile(doc, f);
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setEditingDoc(doc); setShowDocModal(true); }}
                            className="p-1 text-gray-400 hover:text-blue-400"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button onClick={() => handleDeleteDocument(doc.id)} className="p-1 text-gray-400 hover:text-red-400">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Group Modal */}
      {showGroupModal && (
        <ClientDocGroupModal
          group={editingGroup}
          onClose={() => { setShowGroupModal(false); setEditingGroup(null); }}
          onSave={editingGroup ? handleUpdateGroup : handleCreateGroup}
        />
      )}

      {/* Document Modal */}
      {showDocModal && (
        <ClientDocModal
          document={editingDoc}
          groupId={selectedGroupId}
          sectors={sectors}
          onClose={() => { setShowDocModal(false); setEditingDoc(null); }}
          onSave={editingDoc ? handleUpdateDocument : handleCreateDocument}
        />
      )}
    </div>
  );
};

const ClientDocGroupModal = ({ group, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: group?.name || '',
    description: group?.description || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...formData, is_active: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{group ? 'Editar Cliente' : 'Novo Cliente'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const ClientDocModal = ({ document, groupId, sectors, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: document?.name || '',
    edition: document?.edition || '',
    area_id: document?.area_id || '',
    storage_type: document?.storage_type || 'year',
    access_area_ids: document?.access_area_ids || [],
    retention_min: document?.retention_min || 'annual',
    disposal: document?.disposal || 'annual',
    expiration_date: document?.expiration_date || '',
  });

  const toggleAccessArea = (sectorId) => {
    setFormData((prev) => ({
      ...prev,
      access_area_ids: prev.access_area_ids.includes(sectorId)
        ? prev.access_area_ids.filter((id) => id !== sectorId)
        : [...prev.access_area_ids, sectorId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Informe o nome do documento');
      return;
    }
    const dateError = validateDateField(formData.expiration_date, 'Data de vencimento');
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.area_id) delete data.area_id;
      if (!data.expiration_date) delete data.expiration_date;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{document ? 'Editar Documento' : 'Novo Documento'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Edição</label>
              <input
                type="text"
                value={formData.edition}
                onChange={(e) => setFormData({ ...formData, edition: e.target.value })}
                className="input w-full"
                placeholder="Ex: 2ª"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Área</label>
              <select
                value={formData.area_id}
                onChange={(e) => setFormData({ ...formData, area_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {sectors.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Armazenamento</label>
              <select
                value={formData.storage_type}
                onChange={(e) => setFormData({ ...formData, storage_type: e.target.value })}
                className="input w-full"
              >
                <option value="year">Por Ano</option>
                <option value="certificate">Por Certificado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Retenção Mínima</label>
              <select
                value={formData.retention_min}
                onChange={(e) => setFormData({ ...formData, retention_min: e.target.value })}
                className="input w-full"
              >
                <option value="annual">Anual</option>
                <option value="biennial">Bienal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Disposição</label>
              <select
                value={formData.disposal}
                onChange={(e) => setFormData({ ...formData, disposal: e.target.value })}
                className="input w-full"
              >
                <option value="annual">Anual</option>
                <option value="monthly">Mensal</option>
                <option value="semiannual">Semestral</option>
                <option value="quarterly">Trimestral</option>
                <option value="valid">Vigente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data de Vencimento</label>
              <input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Áreas que podem acessar</label>
            <p className="text-xs text-gray-500 mb-2">Selecione os setores que podem visualizar este documento. Nenhuma seleção = todos os setores.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-3 border border-gray-700 rounded-lg">
              {sectors.length === 0 ? (
                <p className="text-sm text-gray-500 col-span-full">Nenhum setor cadastrado</p>
              ) : (
                sectors.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 min-w-0 text-sm text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.access_area_ids.includes(s.id)}
                      onChange={() => toggleAccessArea(s.id)}
                      className="form-checkbox text-green-500 flex-shrink-0"
                    />
                    <span className="truncate min-w-0" title={s.name}>{s.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ClientQualification;